import { type ClassValue, clsx } from 'clsx';
import dayjs from 'dayjs';
import type { ConfigType } from 'dayjs';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export const formatDate = (
  value?: string | Date | null,
  format = 'YYYY-MM-DD HH:mm:ss',
  fallback = '--'
) => {
  if (!value) return fallback;

  const date = dayjs(value);

  return date.isValid() ? date.format(format) : fallback;
};

export const toInputDate = (value?: ConfigType, fallback = ''): string => {
  const date = dayjs(value);

  return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss') : fallback;
};

export const parseInputDate = (value?: string): Date | undefined => {
  if (!value) return undefined;

  const date = dayjs(value);

  return date.isValid() ? date.toDate() : undefined;
};

export const formatReportDate = (
  value?: string | Date | null,
  includeTime = true,
  fallback = '--'
) => formatDate(value, includeTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD', fallback);

export const getNumberValue = (value: unknown, fallback = 0): number => {
  const parsed = Number(value ?? fallback);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const BET_TYPE_LABELS: Record<number, string> = {
  1: 'SINGLE',
  2: 'DOUBLE',
  3: 'TRIPLE',
  4: 'QUADRUPLE',
  5: 'FIVEFOLD',
  6: 'SIXFOLD',
  7: 'SEVENFOLD',
  8: 'EIGHTFOLD',
  9: 'NINEFOLD',
  10: 'TENFOLD',
};

export const getParlayBetTypeLabel = (legCount: number, fallback = 'PARLAY'): string => {
  if (!Number.isFinite(legCount) || legCount <= 0) return fallback;

  return BET_TYPE_LABELS[legCount] ?? `${legCount} FOLD`;
};

export const getBetOnDisplayLabel = (betOn?: string | null, marketType?: string | null) => {
  const betOnValue = String(betOn ?? '--').toLowerCase();
  const marketTypeValue = String(marketType ?? '').toUpperCase();

  if (marketTypeValue === 'FANCY') {
    if (betOnValue === 'back') return 'Yes';
    if (betOnValue === 'lay') return 'No';
  }

  return betOnValue;
};

export const getTextValue = <TRow extends Record<string, unknown>>(
  row: TRow,
  keys: string[],
  fallback = '--'
): string => {
  for (const key of keys) {
    const value = row[key];

    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value);
    }
  }

  return fallback;
};

export const getHasMorePages = (pagination?: {
  currentPage?: number;
  totalPage?: number;
  totalItems?: number;
  limit?: number;
}) => {
  if (!pagination) return false;

  const currentPage = Number(pagination.currentPage ?? 1);
  const totalPage = Number(pagination.totalPage ?? 1);

  return currentPage < totalPage;
};

export function formatCurrency(amount?: number | null, fallback = '-', fractionDigits = 2): string {
  if (amount == null || Number.isNaN(amount)) return fallback;

  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

export function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  } else {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'absolute';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      textArea.remove();
      return Promise.resolve(true);
    } catch (error) {
      textArea.remove();
      return Promise.resolve(false);
    }
  }
}

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export const valueFormatter = Intl.NumberFormat('en', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
});

export const formatNumberWithSuffix = (value?: number | string | null, fallback = '--'): string => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return fallback;
  }

  return valueFormatter.format(number);
};

export const getBlockedStatus = (status?: string, marketStatus?: string): string | null => {
  const rStatus = status?.toUpperCase();
  const mStatus = marketStatus?.toUpperCase();

  const valid = ['OPEN', 'ACTIVE', ''];

  const isRunnerActive = valid.includes(rStatus ?? '');
  const isMarketActive = valid.includes(mStatus ?? '');

  if (isRunnerActive && isMarketActive) return null;

  if (!isRunnerActive && !isMarketActive) return rStatus ?? null;

  if (!isRunnerActive) return rStatus ?? null;

  return mStatus ?? null;
};

export const getPlatform = (): 'Mobile' | 'Desktop' => {
  if (typeof window === 'undefined') return 'Desktop';

  const userAgent = navigator.userAgent.toLowerCase();

  const isMobile = /iphone|ipad|ipod|android|blackberry|opera mini|iemobile|mobile/i.test(
    userAgent
  );

  return isMobile ? 'Mobile' : 'Desktop';
};

export const hasRequiredFields = <T extends Record<string, unknown>>(
  fields: T
): fields is T & {
  [K in keyof T]-?: Exclude<T[K], null | undefined | ''>;
} => {
  return Object.values(fields).every(
    (value) => value !== undefined && value !== null && value !== ''
  );
};
